import ApiService from '../../../core/services/ApiService';
import {
  BulkAddPartnerItemDto,
  BulkAddPartnersResultDto,
  CreateRelationshipDto,
  EntityReltnDto,
  ExitPartnerResultDto,
  GenerateFromRosterResultDto,
  PartnerImportResultDto,
  PartnerOfPartnershipDto,
  PartnershipSummaryDto,
  TierNodeDto,
  UpdateOwnershipDto,
  UpdateRelationshipDto,
} from '../types/tiering.types';

export interface TieringSummary {
  entityId: number;
  entityName: string;
  directPartnerCount: number;
  subFundCount: number;
  maxDepth: number;
  renderMode: 'full' | 'clustered' | 'canvas';
  partnersByCategory: Record<string, number>;
}

export interface TieringTreeNode {
  entityId: number;
  entityName: string;
  entityType: string;
  category: string;
  einSsn: string;
  ownershipPct: number;
  partnerType: string;
  tierLevel: number;
  isCluster: boolean;
  clusterCount: number;
  clusterCategory: string;
  children: TieringTreeNode[];
}

export interface TieringPartner {
  partnerId: number;
  partnerName: string;
  einSsn: string;
  category: string;
  ownershipPct: number;
  interestClass: string;
  status: string;
  city: string;
  state: string;
}

export interface TieringPartnerPage {
  totalCount: number;
  page: number;
  pageSize: number;
  partners: TieringPartner[];
}

class TieringService extends ApiService {
  private basePath = '/api/tiering';

  getSummary(entityId: number): Promise<TieringSummary> {
    return this.makeRequest<TieringSummary>(
      `${this.basePath}/summary/${entityId}`,
      { extractData: true },
    );
  }

  getTree(entityId: number, maxDepth = 5): Promise<TieringTreeNode> {
    return this.makeRequest<TieringTreeNode>(
      `${this.basePath}/tree/${entityId}?maxDepth=${maxDepth}`,
      { extractData: true },
    );
  }

  getPartnersByCategory(
    entityId: number,
    category: string,
    page = 1,
    pageSize = 50,
  ): Promise<TieringPartnerPage> {
    return this.makeRequest<TieringPartnerPage>(
      `${this.basePath}/partners/${entityId}?category=${encodeURIComponent(category)}&page=${page}&pageSize=${pageSize}`,
      { extractData: true },
    );
  }

  getHierarchy(rootEntityId: number): Promise<TierNodeDto> {
    return this.makeRequest<TierNodeDto>(
      `${this.basePath}/hierarchy/${rootEntityId}`,
      { extractData: true },
    );
  }

  getAllRelationships(): Promise<EntityReltnDto[]> {
    return this.makeRequest<EntityReltnDto[]>(
      `${this.basePath}/relationships`,
      { extractData: true },
    );
  }

  getRelationships(entityId: number): Promise<EntityReltnDto[]> {
    return this.makeRequest<EntityReltnDto[]>(
      `${this.basePath}/relationships/${entityId}`,
      { extractData: true },
    );
  }

  createRelationship(dto: CreateRelationshipDto): Promise<EntityReltnDto> {
    return this.makeRequest<EntityReltnDto>(
      `${this.basePath}/relationships`,
      { method: 'post', data: dto as unknown as Record<string, unknown>, extractData: true },
    );
  }

  updateRelationship(id: number, dto: UpdateRelationshipDto): Promise<EntityReltnDto> {
    return this.makeRequest<EntityReltnDto>(
      `${this.basePath}/relationships/${id}`,
      { method: 'put', data: dto as unknown as Record<string, unknown>, extractData: true },
    );
  }

  deleteRelationship(id: number): Promise<void> {
    return this.makeRequest<void>(
      `${this.basePath}/relationships/${id}`,
      { method: 'delete', extractData: true },
    );
  }

  // ── Partner Roster (Session P7) ───────────────────────────────────────────

  getPartnersOfPartnership(partnershipId: number, includeExited = false): Promise<PartnerOfPartnershipDto[]> {
    return this.makeRequest<PartnerOfPartnershipDto[]>(
      `${this.basePath}/partners-of/${partnershipId}?includeExited=${includeExited}`,
      { extractData: true },
    );
  }

  bulkAddPartners(partnershipId: number, items: BulkAddPartnerItemDto[]): Promise<BulkAddPartnersResultDto> {
    return this.makeRequest<BulkAddPartnersResultDto>(
      `${this.basePath}/partners-of/${partnershipId}/bulk`,
      { method: 'post', data: items as unknown as Record<string, unknown>, extractData: true },
    );
  }

  updateOwnership(id: number, dto: UpdateOwnershipDto): Promise<EntityReltnDto> {
    return this.makeRequest<EntityReltnDto>(
      `${this.basePath}/relationships/${id}/ownership`,
      { method: 'put', data: dto as unknown as Record<string, unknown>, extractData: true },
    );
  }

  exitPartner(id: number): Promise<ExitPartnerResultDto> {
    return this.makeRequest<ExitPartnerResultDto>(
      `${this.basePath}/relationships/${id}/exit`,
      { method: 'delete', extractData: true },
    );
  }

  getPartnershipSummary(partnershipId: number): Promise<PartnershipSummaryDto> {
    return this.makeRequest<PartnershipSummaryDto>(
      `${this.basePath}/partnership-summary/${partnershipId}`,
      { extractData: true },
    );
  }

  generateTieringFromRoster(partnershipId: number): Promise<GenerateFromRosterResultDto> {
    return this.makeRequest<GenerateFromRosterResultDto>(
      `${this.basePath}/generate-from-roster/${partnershipId}`,
      { method: 'post', extractData: true },
    );
  }
}

export type { PartnerImportResultDto };

const tieringService = new TieringService();
export default tieringService;
